<template>
  <div class="user-detail">
    <APageHead title="저자 상세" :back="true" @back="$router.back()" />

    <div class="user-detail__layout">
      <aside class="user-detail__side">
        <SideNav :sections="editorSideNav" />
      </aside>
      <main class="user-detail__main">
        <div v-if="loading" class="user-detail__loading">
          <div class="p-spinner p-spinner--lg"></div>
          <p>저자 정보를 불러오는 중...</p>
        </div>

        <template v-else-if="editorData">
          <BasicInfoCard
            id="basic-info"
            :basic-data="editorData"
            user-type="editor"
            @update-field="handleUpdateField"
            @save-changes="handleSaveChanges"
          />
          <EditorWorksCard id="author-works" :editor-idx="editor_idx" />
          <SettlementInfoCard id="settlement-info" :editor-idx="editor_idx" />
          <AccountStatusCard
            id="account-status"
            :current-status="editorData.active_yn"
            :is-employee="false"
            @deactivate-account="handleDeactivateAccount"
            @activate-account="handleActivateAccount"
            @delete-account="handleDeleteAccount"
          />
        </template>

        <AEmpty v-else title="저자 정보를 찾을 수 없어요" />
      </main>
    </div>
  </div>
</template>

<script>
import SideNav from "@/views/users/components/SideNav.vue";
import BasicInfoCard from "@/views/users/components/BasicInfoCard.vue";
import EditorWorksCard from "@/views/users/components/EditorWorksCard.vue";
import SettlementInfoCard from "@/views/users/components/SettlementInfoCard.vue";
import AccountStatusCard from "@/views/users/components/AccountStatusCard.vue";
import APageHead from "@/components/Pineapple/APageHead.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";

export default {
  name: "EditorDetail",
  components: { SideNav, BasicInfoCard, EditorWorksCard, SettlementInfoCard, AccountStatusCard, APageHead, AEmpty },
  data() {
    return {
      editor_idx: null,
      editorData: null,
      loading: false,
      updateFields: {},
      editorSideNav: [
        { id: "basic-info", icon: "receipt_long", label: "기본 정보" },
        { id: "author-works", icon: "library_books", label: "저작물" },
        { id: "settlement-info", icon: "paid", label: "정산 정보" },
        { id: "account-status", icon: "verified_user", label: "계정 상태" },
      ],
    };
  },
  created() {
    this.editor_idx = this.$route.query.idx;
    this.loadEditorData();
  },
  methods: {
    async loadEditorData() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/roles/editors/${this.editor_idx}`,
          loadingBar: true,
        });
        if (res.status === 200) this.editorData = res.data;
      } catch (error) {
        console.error("저자 정보 로드 실패:", error);
        alert("저자 정보를 불러오는데 실패했습니다.");
      } finally {
        this.loading = false;
      }
    },
    handleUpdateField({ field, value }) {
      this.updateFields = { ...this.updateFields, [field]: value };
    },
    async handleSaveChanges() {
      if (Object.keys(this.updateFields).length === 0) {
        alert("변경된 내용이 없습니다.");
        return;
      }
      try {
        await this.$axios({
          method: "put",
          url: `/roles/editors/${this.editor_idx}`,
          data: this.updateFields,
        });
        alert("저자 정보가 성공적으로 수정되었습니다.");
        this.updateFields = {};
        await this.loadEditorData();
      } catch (error) {
        const msg = error.response?.data?.detail || error.response?.data?.message || "저자 정보 수정에 실패했습니다.";
        alert(msg);
      }
    },
    async patchStatus(status, reason, message) {
      try {
        await this.$axios({
          method: "patch",
          url: `/roles/editors/${this.editor_idx}/status`,
          data: { status, reason },
          loadingBar: true,
        });
        alert(message);
        await this.loadEditorData();
      } catch (error) {
        alert(error.response?.data?.detail || "처리에 실패했습니다.");
      }
    },
    handleDeactivateAccount() {
      if (window.confirm("정말 이 계정을 비활성화하시겠습니까?"))
        this.patchStatus("N", "비활성화 요청", "계정이 비활성화되었습니다.");
    },
    handleActivateAccount() {
      if (window.confirm("정말 이 계정을 활성화하시겠습니까?"))
        this.patchStatus("Y", "활성화 요청", "계정이 활성화되었습니다.");
    },
    handleDeleteAccount() {
      if (window.confirm("정말 이 계정을 삭제하시겠습니까?"))
        this.patchStatus("D", "탈퇴 요청", "계정이 삭제되었습니다.");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.user-detail {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__layout {
    display: grid;
    grid-template-columns: 240px 1fr;
    gap: 16px;
    @media (max-width: 992px) { grid-template-columns: 1fr; }
  }

  &__side { min-width: 0; }
  &__main { display: flex; flex-direction: column; gap: 14px; min-width: 0; }
  &__loading {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    padding: 64px 24px; color: $p-ink-mid;
    background: $p-panel; border: 1px solid $p-line; border-radius: $p-radius-lg;
    p { margin: 0; font-size: 12px; }
  }
}
</style>
